import { runAutomation } from "../automation/scheduler.js";
import { runCalendars } from "../calendar/runtime.js";
import { commitEvents, emptyEventJournal, initialRecords, readEvents } from "../domain/state.js";
import { DomainEventSubscriptions } from "../domain/subscriptions.js";
import { validateAllocations } from "../economy/allocations.js";
import { runFlows } from "../economy/flows.js";
import { definitionOwner, definitionScopes } from "../model/definition.js";
import { resolveTriggers, resolveWin } from "../progression/features.js";
import { RandomStreams } from "../random/xoshiro.js";
import { planAdvance } from "../simulation/clock.js";
import { runSteppedRules } from "../simulation/rules.js";
import { runTasks } from "../tasks/runtime.js";
import { mutationDuringEventDelivery } from "./failures.js";
import { cloneProgression, initialProgression } from "./progression-state.js";
import { restoreSnapshot } from "./restore.js";
import { makeSnapshot } from "./snapshot.js";
import { SnapshotSubscriptions } from "./subscriptions.js";
import { cloneCalendars, cloneTasks, initialCalendars, initialMarkets, initialTasks, } from "./timed-state.js";
import { failureFrom, makeTransaction, setTransactionTime } from "./transaction.js";
import { createTransactionWorkspace } from "./workspace.js";
export function createGame(definition, options = {}) {
    if (!definition.numbers || !definition.resources)
        throw new TypeError("Game definition was not created by createGameKit");
    return Object.freeze(new GameRuntime(definition, options.snapshot));
}
class GameRuntime {
    #definition;
    #owner;
    #subscriptions = new SnapshotSubscriptions();
    #eventSubscriptions = new DomainEventSubscriptions();
    #snapshot;
    constructor(definition, restored) {
        this.#definition = definition;
        this.#owner = definitionOwner(definition);
        const initial = makeSnapshot({
            revision: 0n,
            gameTimeMs: 0,
            remainderMs: 0,
            resources: Object.fromEntries(this.#definition.resources.map((resource) => [resource.id, resource.initial])),
            purchaseCounts: Object.fromEntries(this.#definition.buyables.map((buyable) => [buyable.id, buyable.initialCount])),
            allocations: Object.fromEntries(this.#definition.allocations.map((allocation) => [allocation.id, allocation.initial])),
            productionTotals: Object.fromEntries(this.#definition.resources.map((resource) => [
                resource.id,
                this.#definition.numbers.fromNumber(0),
            ])),
            scopeGenerations: initialScopeGenerations(this.#definition),
            progression: initialProgression(),
            random: new RandomStreams(this.#definition.rootSeed ?? "00").snapshot(),
            tasks: initialTasks(this.#definition),
            calendars: initialCalendars(this.#definition),
            markets: initialMarkets(this.#definition),
            records: initialRecords(this.#definition),
            domainEventJournal: emptyEventJournal(),
        });
        this.#snapshot = restoreSnapshot(this.#definition, restored ?? initial);
    }
    getDefinition() {
        return this.#definition;
    }
    getSnapshot() {
        return this.#snapshot;
    }
    dispatch(command) {
        if (this.#eventSubscriptions.publishing)
            return mutationDuringEventDelivery();
        if (command.expectedRevision !== undefined &&
            command.expectedRevision !== this.#snapshot.revision) {
            return {
                ok: false,
                error: {
                    code: "stale-revision",
                    expected: command.expectedRevision,
                    current: this.#snapshot.revision,
                },
            };
        }
        for (const [scopeId, expected] of Object.entries(command.expectedScopeGenerations ?? {})) {
            const current = this.#snapshot.scopeGenerations[scopeId] ?? 0n;
            if (expected !== current) {
                return { ok: false, error: { code: "stale-revision", expected, current } };
            }
        }
        const result = this.transact(command.execute);
        return result.ok
            ? { ok: true, value: { commandId: command.id, revision: result.value.revision } }
            : result;
    }
    advance(elapsedMs, step) {
        if (this.#eventSubscriptions.publishing)
            return mutationDuringEventDelivery();
        const plan = planAdvance(this.#snapshot, elapsedMs, this.#definition.stepMs);
        if (plan.gameTimeMs === this.#snapshot.gameTimeMs &&
            plan.remainderMs === this.#snapshot.remainderMs) {
            return { ok: true, value: this.#snapshot };
        }
        if (plan.steps === 0) {
            this.commit(this.#snapshot.resources, this.#snapshot.gameTimeMs, plan.remainderMs);
            return { ok: true, value: this.#snapshot };
        }
        return this.transact((transaction) => {
            for (let index = 0; index < plan.steps; index += 1) {
                const seconds = this.#definition.stepMs / 1000;
                const boundaryMs = this.#snapshot.gameTimeMs + (index + 1) * this.#definition.stepMs;
                setTransactionTime(transaction, boundaryMs);
                runFlows(transaction, this.#definition.flows, this.#definition.numbers, seconds);
                runSteppedRules(transaction, this.#definition.steppedRules, seconds);
                step?.(transaction, seconds);
                runTasks(transaction, this.#definition.tasks, this.#definition.stepMs);
                resolveTriggers(transaction, this.#definition.triggers);
                runAutomation(transaction, this.#definition.automation, boundaryMs);
                resolveTriggers(transaction, this.#definition.triggers);
                resolveWin(transaction, this.#definition.win);
                runCalendars(transaction, this.#definition.calendars, this.#definition.stepMs, boundaryMs);
            }
        }, plan.gameTimeMs, plan.remainderMs);
    }
    advanceCustom(elapsedMs, apply) {
        if (this.#eventSubscriptions.publishing)
            return mutationDuringEventDelivery();
        const plan = planAdvance(this.#snapshot, elapsedMs, this.#definition.stepMs);
        const advancedGameMs = plan.gameTimeMs - this.#snapshot.gameTimeMs;
        if (advancedGameMs === 0) {
            if (plan.remainderMs !== this.#snapshot.remainderMs)
                this.commit(this.#snapshot.resources, this.#snapshot.gameTimeMs, plan.remainderMs);
            return { ok: true, value: this.#snapshot };
        }
        return this.transact((transaction) => {
            setTransactionTime(transaction, plan.gameTimeMs);
            apply(transaction, advancedGameMs);
        }, plan.gameTimeMs, plan.remainderMs);
    }
    subscribe(selector, listener, equal = Object.is) {
        return this.#subscriptions.subscribe(this.#snapshot, selector, listener, equal);
    }
    readDomainEvents(afterSequence = 0n) {
        return readEvents(requiredJournal(this.#snapshot), afterSequence);
    }
    subscribeDomainEvents(listener) {
        return this.#eventSubscriptions.subscribe(listener);
    }
    transact(execute, gameTimeMs = this.#snapshot.gameTimeMs, remainderMs = this.#snapshot.remainderMs) {
        const workspace = createTransactionWorkspace(this.#snapshot);
        const { resources, purchaseCounts, allocations, productionTotals, scopeGenerations, progression, random, tasks, calendars, markets, records, pendingEvents, } = workspace;
        try {
            const transaction = makeTransaction(this.#owner, resources, purchaseCounts, allocations, productionTotals, scopeGenerations, this.#definition, progression, random, tasks, calendars, markets, records, pendingEvents, this.#snapshot.gameTimeMs, this.#definition.numbers);
            execute(transaction);
            validateAllocations(transaction, this.#definition.allocations);
            resolveTriggers(transaction, this.#definition.triggers);
            resolveWin(transaction, this.#definition.win);
        }
        catch (error) {
            return { ok: false, error: failureFrom(error) };
        }
        this.commit(resources, gameTimeMs, remainderMs, purchaseCounts, allocations, productionTotals, scopeGenerations, progression, random.snapshot(), tasks, calendars, markets, records, requiredJournal(this.#snapshot), pendingEvents);
        return { ok: true, value: this.#snapshot };
    }
    commit(resources, gameTimeMs, remainderMs, purchaseCounts = this.#snapshot.purchaseCounts, allocations = this.#snapshot.allocations, productionTotals = this.#snapshot.productionTotals, scopeGenerations = this.#snapshot.scopeGenerations, progression = cloneProgression(this.#snapshot.progression), random = this.#snapshot.random, tasks = cloneTasks(this.#snapshot.tasks), calendars = cloneCalendars(this.#snapshot.calendars), markets = { ...this.#snapshot.markets }, records = this.#snapshot.records ?? {}, journal = requiredJournal(this.#snapshot), pendingEvents = []) {
        const events = commitEvents(journal, pendingEvents, this.#definition.eventRetention);
        this.#snapshot = makeSnapshot({
            revision: this.#snapshot.revision + 1n,
            gameTimeMs,
            remainderMs,
            resources,
            purchaseCounts,
            allocations,
            productionTotals,
            scopeGenerations,
            progression,
            random,
            tasks,
            calendars,
            markets,
            records,
            domainEventJournal: events.journal,
        });
        this.#subscriptions.publish(this.#snapshot);
        if (events.committed.length > 0)
            this.#eventSubscriptions.publish(events.committed);
    }
}
function requiredJournal(snapshot) {
    return snapshot.domainEventJournal ?? emptyEventJournal();
}
function initialScopeGenerations(definition) {
    return Object.fromEntries(definitionScopes(definition).map((scope) => [scope.id, 0n]));
}
//# sourceMappingURL=game.js.map